import { FieldError, FieldHint, Label } from '@recipes/ui';
import { cloneElement, isValidElement, useId, type ReactElement, type ReactNode } from 'react';

/**
 * Label + control + hint + error wrapper. The child control receives id, aria-invalid
 * and aria-describedby so screen readers announce hints and errors.
 */
export function FormField({
  label,
  hint,
  error,
  required,
  children,
  className,
  optionalText,
}: {
  label: ReactNode;
  hint?: ReactNode;
  error?: string | undefined;
  required?: boolean;
  children: ReactElement<Record<string, unknown>>;
  className?: string;
  optionalText?: string;
}) {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;
  const control = isValidElement(children)
    ? cloneElement(children, {
        id,
        'aria-describedby': describedBy,
        'aria-invalid': error ? true : undefined,
        'aria-required': required || undefined,
      })
    : children;
  return (
    <div className={className}>
      <Label htmlFor={id} className="mb-1 block">
        {label}
        {!required && optionalText && (
          <span className="ml-1 font-normal text-muted-foreground">({optionalText})</span>
        )}
      </Label>
      {control}
      <FieldHint id={hintId}>{hint}</FieldHint>
      <FieldError id={errorId}>{error}</FieldError>
    </div>
  );
}
